<template>
  <div class="app-container padding staff-repair-page">
    <el-card>
      <div slot="header" class="flex justify-between align-center">
        <span>员工报修</span>
      </div>

      <el-form
        ref="form"
        :model="formData"
        :rules="rules"
        label-width="120px"
        class="staff-repair-form"
      >
        <!-- 报修类型（单独一行） -->
        <el-form-item label="报修类型" prop="orderType">
          <el-radio-group
            v-model="formData.orderType"
            @change="handleOrderTypeChange"
          >
            <el-radio
              v-for="item in repairScopes"
              :key="item.id"
              :label="item.templateCode"
            >
              {{ item.name }}
            </el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 报修方式（单独一行，公区报修时不显示） -->
        <el-form-item
          label="报修方式"
          prop="repairMode"
          v-if="formData.orderType === 0"
        >
          <el-radio-group
            v-model="formData.repairMode"
            @change="handleRepairModeChange"
          >
            <el-radio :label="1">帮客户报</el-radio>
            <el-radio :label="2">自己上报</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 室内报修：选择楼栋单元房号（一行排列） -->
        <template v-if="formData.orderType === 0">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="选择楼栋" prop="buildingId">
                <el-select
                  v-model="formData.buildingId"
                  placeholder="请选择楼栋"
                  style="width: 100%"
                  @change="handleBuildingChange"
                >
                  <el-option
                    v-for="item in buildings"
                    :key="item.id"
                    :label="item.floorNum"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="选择单元" prop="unitId">
                <el-select
                  v-model="formData.unitId"
                  placeholder="请选择单元"
                  style="width: 100%"
                  :disabled="!formData.buildingId"
                  @change="handleUnitChange"
                >
                  <el-option
                    v-for="item in units"
                    :key="item.id"
                    :label="item.unitNum"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="选择房号" prop="roomId">
                <el-select
                  v-model="formData.roomId"
                  placeholder="请选择房号"
                  style="width: 100%"
                  filterable
                  :disabled="!formData.unitId"
                  @change="handleRoomChange"
                >
                  <el-option
                    v-for="item in rooms"
                    :key="item.roomId"
                    :label="`${item.roomNum}${
                      item.ownerName ? '(' + item.ownerName + ')' : ''
                    }`"
                    :value="item.roomId"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <!-- 公区报修：详细位置 -->
        <el-form-item
          label="详细位置"
          prop="addressDetail"
          v-if="formData.orderType === 1"
        >
          <el-input
            v-model="formData.addressDetail"
            placeholder="请输入详细位置描述"
            maxlength="200"
          />
        </el-form-item>

        <!-- 报修分类：报修部位选择 -->
        <el-form-item label="报修部位" prop="itemId">
          <el-input
            v-model="selectedItemName"
            placeholder="请选择报修部位"
            readonly
            style="width: 100%"
            @click.native="showCategoryDialog = true"
          >
            <i slot="suffix" class="el-input__icon el-icon-arrow-down"></i>
          </el-input>
        </el-form-item>

        <!-- 所属专业（只读显示） -->
        <el-form-item label="所属专业" v-if="selectedProfessionName">
          <span class="profession-name">{{ selectedProfessionName }}</span>
        </el-form-item>

        <!-- 报修分类选择弹窗 -->
        <el-dialog
          title="选择报修部位"
          :visible.sync="showCategoryDialog"
          width="70%"
          append-to-body
          class="category-dialog"
        >
          <div v-loading="categoryLoading" class="category-content">
            <div
              v-if="categoryData.length === 0 && !categoryLoading"
              class="empty-category"
            >
              暂无数据
            </div>
            <div v-else class="category-sections">
              <div
                v-for="(profession, pIndex) in categoryData"
                :key="pIndex"
                class="category-section"
              >
                <div class="tag-grid">
                  <div
                    class="tag-item profession-tag"
                    :class="{
                      'tag-active':
                        tempProfessionId === profession.professionId,
                    }"
                    @click="selectProfession(profession)"
                  >
                    {{ profession.professionName }}
                  </div>
                </div>
                <div class="tag-grid">
                  <div
                    v-for="(item, iIndex) in profession.items"
                    :key="iIndex"
                    class="tag-item"
                    :class="{ 'tag-active': tempItemId === item.itemId }"
                    @click="selectCategory(profession, item)"
                  >
                    {{ item.itemName }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div slot="footer">
            <el-button @click="closeCategoryDialog">取消</el-button>
            <el-button type="primary" @click="confirmCategory">确定</el-button>
          </div>
        </el-dialog>

        <!-- 报修人信息 -->
        <el-form-item label="报修人" prop="initiatorName">
          <el-input
            v-model="formData.initiatorName"
            placeholder="请输入报修人姓名"
            maxlength="30"
          />
        </el-form-item>

        <el-form-item label="联系电话" prop="initiatorPhone">
          <el-input
            v-model="formData.initiatorPhone"
            type="tel"
            placeholder="请输入手机号"
            maxlength="11"
          />
        </el-form-item>

        <!-- 预约时间（一行排列） -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="预约日期" prop="bookingDate">
              <el-date-picker
                v-model="formData.bookingDate"
                type="date"
                placeholder="请选择预约日期"
                style="width: 100%"
                :picker-options="datePickerOptions"
                value-format="yyyy-MM-dd"
                @change="handleDateChange"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="预约时间" prop="bookingTime">
              <el-time-picker
                v-model="formData.bookingTime"
                placeholder="请选择预约时间"
                style="width: 100%"
                value-format="HH:mm"
                format="HH:mm"
                :picker-options="timePickerOptions"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 问题描述 -->
        <el-form-item label="问题描述" prop="problemDesc">
          <el-input
            v-model="formData.problemDesc"
            type="textarea"
            :rows="4"
            placeholder="请详细描述故障情况，以便维修人员准备工具和配件"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>

        <!-- 图片上传 -->
        <el-form-item label="上传图片" class="upload-form-item">
          <el-upload
            action=""
            list-type="picture-card"
            :file-list="imageList"
            :on-preview="handlePictureCardPreview"
            :on-remove="handleRemove"
            :auto-upload="true"
            :http-request="uploadImage"
            :limit="9"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
          <div class="upload-tip">最多上传9张图片</div>
        </el-form-item>

        <div class="form-footer">
          <el-button type="primary" :loading="submitting" @click="handleSubmit">
            提交报修
          </el-button>
        </div>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import {
  saveOrUpdateRepairOrder,
  complaintChangeRepair,
} from "@/api/work/repairPoolManageApi";
import { listRepairFlowTemplates } from "@/api/work/repairFlowTemplateApi";
import { listRepairCategories } from "@/api/work/skillApi";
import { queryFloorAndUnits } from "@/api/room/roomStructureApi";
import { queryRooms } from "@/api/room/roomApi";
import { getCommunityId, getCommunityName } from "@/api/community/communityApi";
import { uploadImage as uploadOwnerImage } from "@/api/owner/ownerApi";

export default {
  name: "StaffRepairPage",
  data() {
    return {
      submitting: false,
      repairScopes: [],
      buildings: [],
      units: [],
      rooms: [],
      categoryData: [],
      categoryLoading: false,
      showCategoryDialog: false,
      selectedProfessionName: "",
      selectedItemName: "",
      tempProfessionId: null,
      tempProfessionName: "",
      tempItemId: null,
      tempItemName: "",
      imageList: [],
      datePickerOptions: {
        disabledDate(time) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return time.getTime() < today.getTime();
        },
      },
      timePickerOptions: {
        selectableRange: "08:00:00 - 22:00:00",
      },
      formData: {
        orderType: 0, // 0-室内报修, 1-公区报修
        templateId: null,
        repairMode: 1, // 1-帮客户报, 2-自己上报
        sourceType: 2, // 2-其他员工代报, 3-工程自报(跳过派单)
        initiatorType: 1, // 1-业主, 2-员工
        buildingId: null,
        unitId: null,
        roomId: null,
        addressDetail: "",
        professionId: null,
        itemId: null,
        initiatorName: "",
        initiatorPhone: "",
        bookingDate: "",
        bookingTime: "",
        problemDesc: "",
        problemImgs: "",
      },
      rules: {
        orderType: [
          { required: true, message: "请选择报修类型", trigger: "change" },
        ],
        buildingId: [
          {
            required: true,
            message: "请选择楼栋",
            trigger: "change",
            validator: (rule, value, callback) => {
              if (this.formData.orderType === 0 && !value) {
                callback(new Error("请选择楼栋"));
              } else {
                callback();
              }
            },
          },
        ],
        unitId: [
          {
            required: true,
            message: "请选择单元",
            trigger: "change",
            validator: (rule, value, callback) => {
              if (this.formData.orderType === 0 && !value) {
                callback(new Error("请选择单元"));
              } else {
                callback();
              }
            },
          },
        ],
        roomId: [
          {
            required: true,
            message: "请选择房号",
            trigger: "change",
            validator: (rule, value, callback) => {
              if (this.formData.orderType === 0 && !value) {
                callback(new Error("请选择房号"));
              } else {
                callback();
              }
            },
          },
        ],
        addressDetail: [
          {
            required: true,
            message: "请填写详细位置",
            trigger: "blur",
            validator: (rule, value, callback) => {
              if (this.formData.orderType === 1 && !value) {
                callback(new Error("请填写详细位置"));
              } else {
                callback();
              }
            },
          },
        ],
        itemId: [
          { required: true, message: "请选择报修部位", trigger: "change" },
        ],
        initiatorName: [
          { required: true, message: "请填写报修人", trigger: "blur" },
          { max: 30, message: "报修人长度不能超过30位", trigger: "blur" },
        ],
        initiatorPhone: [
          { required: true, message: "请填写联系电话", trigger: "blur" },
          {
            pattern: /^1[3-9]\d{9}$/,
            message: "请输入正确的手机号",
            trigger: "blur",
          },
        ],
        bookingDate: [
          { required: true, message: "请选择预约日期", trigger: "change" },
        ],
        bookingTime: [
          { required: true, message: "请选择预约时间", trigger: "change" },
        ],
        problemDesc: [
          { required: true, message: "请填写问题描述", trigger: "blur" },
        ],
      },
      staffInfo: null,
      staffUserId: "",
      staffUserName: "",
      staffUserPhone: "",
      // 从投诉单转工单时的原始投诉数据（可用于后续扩展：建立关联等）
      relatedComplaint: null,
    };
  },
  created() {
    this.initStaffInfo();
    this.loadRepairScopes();
    this.loadBuildings();
    this.loadCategoryData();
    this.initFromComplaint();
  },
  methods: {
    resetForm() {
      this.formData = {
        orderType: 0,
        templateId: null,
        repairMode: 1,
        sourceType: 2,
        initiatorType: 1,
        buildingId: null,
        unitId: null,
        roomId: null,
        addressDetail: "",
        professionId: null,
        itemId: null,
        initiatorName: "",
        initiatorPhone: "",
        bookingDate: "",
        bookingTime: "",
        problemDesc: "",
        problemImgs: "",
      };
      this.imageList = [];
      this.units = [];
      this.rooms = [];
      this.selectedProfessionName = "";
      this.selectedItemName = "";
      this.tempProfessionId = null;
      this.tempProfessionName = "";
      this.tempItemId = null;
      this.tempItemName = "";
      this.$refs.form && this.$refs.form.resetFields();
    },
    // 初始化员工信息
    initStaffInfo() {
      try {
        const userStr = localStorage.getItem("user") || "{}";
        const user = JSON.parse(userStr);
        this.staffInfo = user;
        this.staffUserId = user.userId || user.staffId || "";
        this.staffUserName = user.userName || user.staffName || "";
        this.staffUserPhone = user.tel || user.phone || "";

        if (this.formData.repairMode === 2) {
          this.formData.initiatorId = this.staffUserId;
          this.formData.initiatorName = this.staffUserName;
          this.formData.initiatorPhone = this.staffUserPhone;
          this.formData.initiatorType = 2;
        }
      } catch (e) {
        console.log("获取员工信息失败:", e);
      }
    },
    // 如果是从投诉详情“转工单”跳转过来，初始化表单数据
    initFromComplaint() {
      const query = this.$route && this.$route.query ? this.$route.query : {};
      const raw = query.complaintData;
      if (!raw) {
        return;
      }
      try {
        const decoded = decodeURIComponent(raw);
        const data = JSON.parse(decoded);
        this.relatedComplaint = data;

        // 报修方式：默认“帮客户报”
        this.formData.repairMode = 1;
        this.formData.initiatorType = 1;

        // 报修人信息
        this.formData.initiatorName = data.ownerName || "";
        this.formData.initiatorPhone = data.contactPhone || "";

        // 问题描述
        this.formData.problemDesc = data.content || "";

        // 初始化投诉图片到上传列表
        let imgs = [];
        if (Array.isArray(data.problemImgs)) {
          imgs = data.problemImgs;
        } else if (typeof data.problemImgs === "string") {
          imgs = data.problemImgs
            .split(",")
            .map((i) => i && i.trim())
            .filter(Boolean);
        }
        this.imageList = imgs.map((url, index) => ({
          uid: `complaint-img-${index}`,
          name: `complaint-${index}`,
          url,
        }));
      } catch (e) {
        console.log("解析投诉转工单数据失败:", e);
      }
    },
    // 加载报修类型列表
    async loadRepairScopes() {
      try {
        const res = await listRepairFlowTemplates({
          page: 1,
          row: 100,
          isEnable: "1",
		  isAll:0
        });
        this.repairScopes = (res.data || []).map((item) => ({
          id: item.id,
          templateCode: item.templateCode,
          name: item.templateName,
        }));
        if (this.repairScopes.length > 0) {
          this.formData.orderType = this.repairScopes[0].templateCode;
          this.formData.templateId = this.repairScopes[0].id;
        }
      } catch (error) {
        console.log("加载报修类型失败:", error);
        this.$message.error("加载报修类型失败");
      }
    },
    // 报修类型切换
    handleOrderTypeChange(value) {
      const selected = this.repairScopes.find(
        (item) => item.templateCode === value
      );
      if (selected) {
        this.formData.templateId = selected.id;
      }
      if (value === 1) {
        this.formData.repairMode = 2;
        this.formData.initiatorId = this.staffUserId;
        this.formData.initiatorName = this.staffUserName;
        this.formData.initiatorPhone = this.staffUserPhone;
        this.formData.initiatorType = 2;
      }
      this.formData.buildingId = null;
      this.formData.unitId = null;
      this.formData.roomId = null;
      this.formData.addressDetail = "";
      this.units = [];
      this.rooms = [];
      this.updateSourceType();
    },
    // 报修方式切换
    handleRepairModeChange(value) {
      if (value === 1) {
        this.formData.initiatorName = "";
        this.formData.initiatorPhone = "";
        this.formData.initiatorId = "";
        this.formData.initiatorType = 1;
      } else {
        this.formData.initiatorId = this.staffUserId;
        this.formData.initiatorName = this.staffUserName;
        this.formData.initiatorPhone = this.staffUserPhone;
        this.formData.initiatorType = 2;
      }
      this.updateSourceType();
    },
    // 更新 sourceType
    updateSourceType() {
      if (this.formData.repairMode === 1) {
        this.formData.sourceType = 2;
      } else {
        if (this.formData.orderType === 1) {
          const nowRole = this.getNowRole();
          if (nowRole && nowRole.relCd === "3") {
            this.formData.sourceType = 3;
          } else {
            this.formData.sourceType = 2;
          }
        } else {
          this.formData.sourceType = 2;
        }
      }
    },
    // 获取当前角色
    getNowRole() {
      try {
        const savedRelCd =
          localStorage.getItem("repairPoolManage_relCd") || "1";
        const savedRoleKey =
          localStorage.getItem("repairPoolManage_handlerKey") || "";
        return {
          relCd: savedRelCd,
          handlerKey: savedRoleKey,
        };
      } catch (e) {
        return null;
      }
    },
    // 加载楼栋列表
    async loadBuildings() {
      try {
        const res = await queryFloorAndUnits();
        const arr = res.data || res || [];
        const buildingMap = {};
        arr.forEach((item) => {
          if (!buildingMap[item.floorId]) {
            buildingMap[item.floorId] = {
              id: item.floorId,
              floorNum: item.floorNum + "号楼",
              floorNumRaw: item.floorNum,
              units: [],
            };
          }
          if (item.unitId) {
            const existUnit = buildingMap[item.floorId].units.find(
              (u) => u.id === item.unitId
            );
            if (!existUnit) {
              buildingMap[item.floorId].units.push({
                id: item.unitId,
                unitNum: item.unitNum + "单元",
              });
            }
          }
        });
        this.buildings = Object.values(buildingMap);
      } catch (error) {
        console.log("加载楼栋列表失败:", error);
        this.$message.error("加载楼栋列表失败");
      }
    },
    // 楼栋选择
    handleBuildingChange(value) {
      const building = this.buildings.find((b) => b.id === value);
      if (building) {
        this.units = building.units || [];
      } else {
        this.units = [];
      }
      this.formData.unitId = null;
      this.formData.roomId = null;
      this.rooms = [];
    },
    // 单元选择
    handleUnitChange(value) {
      this.formData.roomId = null;
      this.rooms = [];
      if (value) {
        this.loadRooms();
      }
    },
    // 加载房号列表
    async loadRooms() {
      try {
        const res = await queryRooms({
          communityId: getCommunityId(),
          floorId: this.formData.buildingId,
          unitId: this.formData.unitId,
          roomType: "1010301",
          flag: 1,
          page: 1,
          row: 100,
        });
        const arr = res.rooms || res.data || res || [];
        this.rooms = arr.map((item) => ({
          roomId: item.roomId,
          roomNum: item.roomNum || item.roomName,
          ownerName: item.ownerName || "",
          ownerId: item.ownerId || "",
          ownerPhone: item.link || item.ownerPhone || "",
        }));
      } catch (error) {
        console.log("加载房号列表失败:", error);
        this.$message.error("加载房号列表失败");
      }
    },
    // 房号选择
    handleRoomChange(value) {
      if (this.formData.repairMode === 1 && value) {
        const room = this.rooms.find((r) => r.roomId === value);
        if (room && room.ownerName) {
          this.formData.initiatorId = room.ownerId;
          this.formData.initiatorName = room.ownerName;
          this.formData.initiatorPhone = room.ownerPhone;
        }
      }
    },
    // 加载报修分类数据
    async loadCategoryData() {
      try {
        this.categoryLoading = true;
        const res = await listRepairCategories({});
        const data = res.data || [];
        const professions = data.filter((item) => item.categoryType === 1);
        this.categoryData = professions.map((item) => ({
          professionId: item.id,
          professionName: item.name,
          items: (item.childrenCategories || []).map((child) => ({
            itemId: child.id,
            itemName: child.name,
          })),
        }));
      } catch (error) {
        console.log("加载报修分类失败:", error);
        this.$message.error("加载报修分类失败");
      } finally {
        this.categoryLoading = false;
      }
    },
    // 选择专业
    selectProfession(profession) {
      this.tempProfessionId = profession.professionId;
      this.tempProfessionName = profession.professionName;
      this.tempItemId = profession.professionId;
      this.tempItemName = profession.professionName;
    },
    // 选择分类（临时选择）
    selectCategory(profession, item) {
      this.tempProfessionId = profession.professionId;
      this.tempProfessionName = profession.professionName;
      this.tempItemId = item.itemId;
      this.tempItemName = item.itemName;
    },
    // 确认选择分类
    confirmCategory() {
      if (!this.tempItemId) {
        this.$message.warning("请选择报修部位");
        return;
      }
      this.formData.professionId = this.tempProfessionId;
      this.formData.itemId = this.tempItemId;
      this.selectedProfessionName = this.tempProfessionName;
      this.selectedItemName = this.tempItemName;
      this.showCategoryDialog = false;
    },
    // 关闭分类选择弹窗
    closeCategoryDialog() {
      this.tempProfessionId = this.formData.professionId;
      this.tempProfessionName = this.selectedProfessionName;
      this.tempItemId = this.formData.itemId;
      this.tempItemName = this.selectedItemName;
      this.showCategoryDialog = false;
    },
    // 日期选择
    handleDateChange(value) {
      if (value) {
        const today = new Date().toISOString().split("T")[0];
        if (value === today) {
          const now = new Date();
          const hours = String(now.getHours()).padStart(2, "0");
          const minutes = String(now.getMinutes()).padStart(2, "0");
          this.timePickerOptions.selectableRange = `${hours}:${minutes}:00 - 22:00:00`;
        } else {
          this.timePickerOptions.selectableRange = "08:00:00 - 22:00:00";
        }
      }
    },
    // 图片上传
    async uploadImage(param) {
      try {
        const res = await uploadOwnerImage({ file: param.file });
        const url = res && (res.url || (res.data && res.data.url));
        if (!url) {
          throw new Error("上传失败，未返回图片地址");
        }
        const file = param.file;
        file.url = url;
        const index = this.imageList.findIndex((item) => item.uid === file.uid);
        if (index > -1) {
          this.$set(this.imageList, index, file);
        } else {
          this.imageList.push(file);
        }
        param.onSuccess(res);
      } catch (e) {
        console.log("上传图片失败:", e);
        this.$message.error(e.message || "上传图片失败");
        param.onError(e);
      }
    },
    // 图片预览
    handlePictureCardPreview(file) {
      if (file && file.url) {
        window.open(file.url, "_blank");
      }
    },
    // 图片移除
    handleRemove(file) {
      if (!file || !file.url) return;
      const index = this.imageList.findIndex((img) => img.url === file.url);
      if (index > -1) {
        this.imageList.splice(index, 1);
      }
    },
    // 提交报修
    async handleSubmit() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) {
          return false;
        }

        const bookingTime =
          this.formData.bookingDate + " " + this.formData.bookingTime + ":00";
        const bookingTimestamp = new Date(
          bookingTime.replace(/-/g, "/")
        ).getTime();
        const now = Date.now();
        if (bookingTimestamp <= now - 30 * 60 * 1000) {
          this.$message.warning("预约时间不能早于当前时间");
          return false;
        }

        this.submitting = true;
        try {
          this.updateSourceType();

          const params = {
            communityId: getCommunityId(),
            communityName: getCommunityName(),
            orderType: this.formData.orderType,
            templateId: this.formData.templateId,
            sourceType: this.formData.sourceType,
            initiatorId:
              this.formData.repairMode === 1
                ? this.formData.initiatorId || this.staffUserId
                : this.staffUserId,
            initiatorName: this.formData.initiatorName,
            initiatorPhone: this.formData.initiatorPhone,
            initiatorType: this.formData.initiatorType,
            professionId: this.formData.professionId,
            itemId: this.formData.itemId,
            problemDesc: this.formData.problemDesc,
            problemImgs: this.imageList.map((img) => img.url || img).join(","),
            bookingTime: bookingTime,
            status: 0,
          };

          // 如果是从投诉单转工单过来的，可以附带投诉信息，便于后端做关联（字段名由后端决定，额外字段不会影响现有逻辑）
          if (this.relatedComplaint) {
            params.relatedComplaintId =
              this.relatedComplaint.id ||
              this.relatedComplaint.complaintId ||
              "";
            params.relatedComplaintNo = this.relatedComplaint.complaintNo || "";
          }

          if (this.formData.repairMode === 1) {
            params.sourceUserId = this.staffUserId;
          }

          if (this.formData.orderType === 1) {
            params.addressDetail = this.formData.addressDetail;
            if (this.formData.buildingId)
              params.buildingId = this.formData.buildingId;
          } else {
            params.roomId = this.formData.roomId;
            params.buildingId = this.formData.buildingId;
            params.unitId = this.formData.unitId;
            const room = this.rooms.find(
              (r) => r.roomId === this.formData.roomId
            );
            if (room) {
              params.roomName = room.roomNum;
            }
          }

          if (this.relatedComplaint) {
            // 从投诉转工单：走专用投诉转工单接口
            const complaintParams = {
              ...params,
              complaintId:
                this.relatedComplaint.id ||
                this.relatedComplaint.complaintId ||
                "",
              complaintNo: this.relatedComplaint.complaintNo || "",
            };
            await complaintChangeRepair(complaintParams);
          } else {
            // 普通员工报修：走通用报修提交接口
            await saveOrUpdateRepairOrder(params);
          }
          this.$message.success("提交成功，请到工单管理页面查看");
          // 跳转到工单管理页面
          setTimeout(() => {
            this.$router.push({
              path: "/pages/property/repairPoolManage",
            });
          }, 1000);
        } catch (error) {
          console.log("提交报修失败:", error);
          this.$message.error(error.message || "提交失败");
        } finally {
          this.submitting = false;
        }
      });
    },
  },
};
</script>

<style scoped>
.staff-repair-page {
  /* width: 100%; */
  max-width: 100%;
  margin: 0 auto;
  overflow-x: hidden;
}

.upload-tip {
  font-size: 12px;
  color: #999;
  margin-top: 5px;
}

/* 表单左对齐 */
.staff-repair-form ::v-deep .el-form-item__label {
  text-align: left;
}

.staff-repair-form ::v-deep .el-form-item__content {
  text-align: left;
  display: flex;
  align-items: center;
}

.staff-repair-form ::v-deep .el-radio-group {
  display: flex;
  justify-content: flex-start;
  height: 40px;
  align-items: center;
}

.profession-name {
  color: #666;
  font-size: 14px;
}

/* 报修分类选择弹窗样式 */
.category-content {
  max-height: 500px;
  overflow-y: auto;
}

.empty-category {
  text-align: center;
  padding: 60px 0;
  color: #999;
  font-size: 14px;
}

.category-sections {
  padding: 0;
}

.category-section {
  margin-bottom: 24px;
}

.category-section:last-child {
  margin-bottom: 0;
}

.category-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  padding-left: 12px;
  position: relative;
}

.category-name::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 16px;
  background: #409eff;
  border-radius: 2px;
}

.tag-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.category-section:last-child .tag-grid {
  border-bottom: none;
}

.tag-item {
  padding: 8px 16px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid transparent;
}

.tag-item:hover {
  background: #e6f2ff;
  color: #409eff;
}

.tag-item.tag-active {
  background: #e6f2ff;
  color: #409eff;
  border-color: #409eff;
}

/* 专业标签样式 */
.profession-tag {
  background: #ecf5ff;
  font-weight: 600;
  margin-right: 0;
  padding: 10px 20px;
  font-size: 15px;
}

.form-footer {
  text-align: right;
  margin-top: 10px;
}

/* 上传图片：让提示文字显示在图片下面 */
.upload-form-item ::v-deep .el-form-item__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.upload-tip {
  margin-top: 8px;
}
</style>

